import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ProviderLayout from '../../components/ProviderLayout';
import { apiFetch, API_BASE_URL } from '../../api';
import { formatPhone } from '../../lib/phone';
import { providerRegion, providerCategories } from '../../lib/providerInfo';
import { formatSom } from '../../lib/money';


// Force absolute https so avatars load reliably.
function httpsUrl(u) {
  if (!u) return null;
  if (u.startsWith('http://')) return 'https://' + u.slice(7);
  if (u.startsWith('/')) return API_BASE_URL + u;
  return u;
}

export default function ProviderProfilePage() {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const user = auth?.user || {};

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch('/api/provider/profile', auth.token);
      if (res.status === 404) {
        setProfile(null); // profil tafsiloti hali to'ldirilmagan — asosiy ma'lumot ko'rsatiladi
        return;
      }
      if (!res.ok) throw new Error();
      const data = await res.json().catch(() => null);
      setProfile(data && typeof data === 'object' ? data : null);
    } catch {
      // Profile detail is optional — still show the basic account info below.
      setProfile(null);
      setError('Profil tafsilotlarini yuklashda xatolik yuz berdi.');
    } finally {
      setLoading(false);
    }
  }, [auth.token]);

  useEffect(() => { load(); }, [load]);

  // Backend profil javobi (GET /api/provider/profile) — asosiy manba; login `user` obyekti zaxira.
  const p = profile || {};
  const fullName =
    [p.firstName ?? user.firstName, p.lastName ?? user.lastName].filter(Boolean).join(' ') || p.fullName || 'Usta';
  const phone = formatPhone(p.phone ?? user.phone);
  // Hudud admin tafsiloti bilan bir xil (region[, district]); bo'sh bo'lsa eski shahar/tuman maydonlari.
  const area =
    providerRegion(p) || [p.serviceAreaCity ?? p.city, p.serviceAreaDistrict].filter(Boolean).join(', ');
  const categories = providerCategories(p).map((c) => c.name).join(', ');
  const credit = p.creditBalance ?? p.credit_balance;
  const avatar = httpsUrl(user.avatarUrl || user.imageUrl || (profile && (profile.avatarUrl || profile.imageUrl)));

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <ProviderLayout title="Profil">
      <div style={s.head}>
        {avatar ? (
          <img src={avatar} alt={fullName} style={s.avatar} />
        ) : (
          <div style={{ ...s.avatar, ...s.avatarEmpty }}>{fullName.charAt(0).toUpperCase()}</div>
        )}
        <div style={s.name}>{fullName}</div>
        {phone && <div style={s.phone}>{phone}</div>}
      </div>

      {loading ? (
        <div style={s.center}>Yuklanmoqda...</div>
      ) : (
        <>
          {error && <div style={s.info}>{error}</div>}
          <div style={s.block}>
            <Row label="Kategoriyalar" value={categories} />
            <Row label="Kredit balansi" value={credit == null || credit === '' ? null : formatSom(credit)} />
            <Row label="Hudud" value={area} />
            <Row label="Manzil" value={p.address ?? user.address} />
            <Row label="Bio" value={p.bio} />
            <Row label="Tajriba (yil)" value={p.experienceYears ?? p.experience} />
          </div>
        </>
      )}

      <button style={s.logout} onClick={handleLogout}>Chiqish</button>
    </ProviderLayout>
  );
}

function Row({ label, value }) {
  return (
    <div style={s.row}>
      <span style={s.rowLabel}>{label}</span>
      <span style={s.rowValue}>
        {value === null || value === undefined || value === '' ? '—' : String(value)}
      </span>
    </div>
  );
}

const s = {
  head: { textAlign: 'center', padding: '16px 0 20px' },
  avatar: {
    width: '84px',
    height: '84px',
    borderRadius: '50%',
    objectFit: 'cover',
    margin: '0 auto 12px',
    display: 'block',
    background: 'var(--color-surface-alt)',
  },
  avatarEmpty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '34px',
    fontWeight: '700',
    color: 'var(--color-on-primary)',
    background: 'var(--color-primary)',
  },
  name: { fontSize: '19px', fontWeight: '700', color: 'var(--color-text)' },
  phone: { fontSize: '14px', color: 'var(--color-text-muted)', marginTop: '4px' },
  center: { textAlign: 'center', padding: '32px', color: 'var(--color-text-muted)' },
  info: {
    background: 'var(--color-primary)',
    borderRadius: 'var(--radius-input)',
    padding: '10px 14px',
    color: 'var(--color-on-primary)',
    fontSize: '13px',
    marginBottom: '12px',
  },
  block: {
    background: 'var(--color-surface-alt)',
    borderRadius: 'var(--radius-card)',
    padding: '4px 16px',
    marginBottom: '24px',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '12px 0',
    borderBottom: '1px solid var(--color-border)',
    fontSize: '14px',
  },
  rowLabel: { color: 'var(--color-text-muted)' },
  rowValue: { color: 'var(--color-text)', fontWeight: '600', textAlign: 'right' },
  logout: {
    width: '100%',
    padding: '13px',
    background: 'var(--color-surface)',
    color: 'var(--color-text)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-btn)',
    minHeight: '48px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
  },
};
